/**
 * 處理所有轉址需求:
 * 1. Global 與 ACN 環境: console 相關頁面轉址
 * 2. Global 環境: partners 和 about-us 頁面轉址
 * 3. Global 環境: /marketplace 舊路由轉址
 * 4. Global 與 ACN 環境: product 相關頁面轉址
 */

export default defineNuxtRouteMiddleware((to) => {
  const runtimeConfig = useRuntimeConfig();
  const { CONFIGURATION, ENVIRONMENT } = runtimeConfig.public;
  const { path, query, hash } = to;

  // split 後的 path segments
  const pathSegments = (() => {
    let processPath = path;
    if (processPath.length > 1 && processPath.slice(-1) === '/') {
      processPath = processPath.slice(0, -1);
    }
    return processPath.split('/').filter(Boolean);
  })();

  // 去除 locale 後的 path
  const pathWithoutLocale =
    pathSegments.length > 1 ? `/${pathSegments.slice(1).join('/')}` : '/';

  const locale = pathSegments[0] || '';

  if (!locale) {
    return;
  }

  /**
   * console 相關頁面轉址
   * 將原本在 Marketplace Profile 的相關頁面轉址到 WISE Console 新的路由。
   */

  const consoleRedirectMap: Record<string, string> = {
    '/marketplace/profile/membership-program': '/membership',
    '/marketplace/profile/gift-value': '/gift-values',
    '/marketplace/profile/billing-history': '/bills',
    '/marketplace/profile/license-management': '/licenses',
    '/marketplace/my-team': '/team-management',
  };

  const consoleRedirectTo = consoleRedirectMap[pathWithoutLocale];

  if (consoleRedirectTo) {
    const consoleDomain = provideConsoleDomain(
      locale as EnumLocale,
      ENVIRONMENT,
      true
    );

    return navigateTo(
      {
        path: `${consoleDomain}/${locale}${consoleRedirectTo}`,
        query,
        hash,
      },
      {
        redirectCode: 301,
        external: true,
      }
    );
  }

  /**
   * partners 和 about-us 頁面轉址
   * 在 Global 環境下，將舊的 partners 和 about-us 頁面轉址到新的路由。
   */

  if (CONFIGURATION !== 'ACN') {
    const globalRedirectMap: Record<string, string> = {
      '/marketplace/partners/Ecosystem': '/ecosystem/list',
      '/marketplace/partners': '/ecosystem/overview',
      '/marketplace/about-us': '/overview',
      '/partners/Ecosystem': '/ecosystem/list',
      '/partners': '/ecosystem/overview',
      '/about-us': '/overview',
    };

    const globalRedirectTo = globalRedirectMap[pathWithoutLocale];

    if (globalRedirectTo) {
      const redirectPath = `/${locale}${globalRedirectTo}`;
      return navigateTo(
        { path: redirectPath, query, hash },
        { redirectCode: 301 }
      );
    }
  }

  /**
   * marketplace 舊路由轉址
   * 在 Global 環境下，所有以 /marketplace 開頭的路由都轉址到沒有 /marketplace 的新路由。
   */

  if (CONFIGURATION !== 'ACN') {
    if (pathWithoutLocale.startsWith('/marketplace')) {
      const newPath =
        pathWithoutLocale === '/marketplace'
          ? ''
          : pathWithoutLocale.replace('/marketplace', '');

      return navigateTo(
        { path: `/${locale}${newPath}`, query, hash },
        { redirectCode: 301 }
      );
    }
  }

  /**
   * product 相關頁面轉址
   *
   * 1. /product/{id} 要轉去 /products/{id}
   * 2. /product/{id}/docs 要轉去 /products/{id}
   * 3. /product/{id}/related-materials 要轉去 /products/{id}
   * 4. /product/{id}/service-architecture 要轉去 /products/{id}
   * 5. /product/{id}/pricing-details 要轉去 /pricing/{id}
   */

  // 去除 locale 與 marketplace 後的 path segments
  const segments = pathSegments.filter((item) => {
    return item !== locale && item !== 'marketplace';
  });

  const isOldProductPage = (() => {
    if (segments[0] === 'product' && segments.length >= 2) {
      return true;
    }
    if (segments[0] === 'product' || segments[0] === 'products') {
      switch (segments[2]) {
        case 'docs':
        case 'related-materials':
        case 'service-architecture':
        case 'pricing-details':
          return true;
      }
    }
    return false;
  })();

  if (isOldProductPage) {
    const redirectPath = (() => {
      const startdPath =
        CONFIGURATION === 'ACN' ? `/${locale}/marketplace` : `/${locale}`;
      const productId = segments[1];

      switch (segments[2]) {
        case 'pricing-details':
          return `${startdPath}/pricing/${productId}`;
        case 'docs':
        case 'related-materials':
        case 'service-architecture':
        default:
          return `${startdPath}/products/${productId}`;
      }
    })();
    return navigateTo(
      { path: redirectPath, query, hash },
      { redirectCode: 301 }
    );
  }
});
